import type { Localized } from "./types";

export const text = (zh: string, en: string): Localized => ({ zh, en });

export const navItems = [
  { href: "#problem", label: text("问题", "Problem") },
  { href: "#solution", label: text("方案", "Solution") },
  { href: "#product", label: text("产品", "Product") },
  { href: "#roadmap", label: text("路线图", "Roadmap") },
  { href: "#funding", label: text("融资", "Funding") },
];

export const problemPoints = [
  text("要自己看盘、分析、下单", "Users must monitor markets, analyze data, and place orders themselves"),
  text("要懂跨链、Gas费、风控", "Users must understand cross-chain operations, gas fees, and risk controls"),
  text("24小时盯盘，身心俱疲", "Markets require 24/7 monitoring, leaving users physically and mentally exhausted"),
  text("大多数人要么亏钱，要么根本不敢进场", "Most people either lose money or are too intimidated to enter the market at all"),
];

export const agents = [
  text("研究Agent", "Research Agent"),
  text("执行Agent", "Execution Agent"),
  text("风控Agent", "Risk Control Agent"),
  text("跨链Agent", "Cross-chain Agent"),
];

export const productSteps = [
  text("输入意图（自然语言）", "Enter an intent in natural language"),
  text("AI快速理解并生成策略", "AI quickly interprets it and generates a strategy"),
  text("清楚显示收益预估与风险控制", "Projected returns and risk controls are clearly displayed"),
  text("用户一键确认", "The user confirms with one click"),
  text("Agent自动执行，即时回报结果", "Agents execute automatically and report results immediately"),
];

export const trends = [
  text("AI Agent开始能真正持有钱包、自主交易", "AI Agents are beginning to truly hold wallets and trade autonomously"),
  text("「意图交易」技术成熟（用户只说结果，不说细节）", "Intent-based trading technology is maturing—users specify outcomes, not operational details"),
  text("跨链技术普及，资金可自动流动", "Cross-chain technology is becoming widespread, allowing capital to move automatically"),
];

export const marketPoints = [
  text("全球加密用户数以亿计", "The number of cryptocurrency users worldwide is measured in the hundreds of millions"),
  text("真正会自己做复杂策略的人不到5%", "Fewer than 5% truly know how to execute complex strategies themselves"),
  text("大多数人想轻松参与，却没有好工具", "Most people want an easier way to participate, but suitable tools do not yet exist"),
];

export const revenueLines = [
  text("交易手续费（人类 + AI Agent交易都收）", "Transaction fees charged on both human and AI Agent trades"),
  text("Agent市场抽成（出租或复制优秀Agent）", "Agent marketplace commissions from renting or copying high-performing Agents"),
  text("高级功能订阅（更强AI、专属数据与风控）", "Premium subscriptions offering more capable AI, exclusive data, and enhanced risk controls"),
  text("Solver竞争费用（专业团队完成意图，平台抽成）", "Solver competition fees, with the platform taking a commission when professional teams fulfill user intents"),
];

export const roadmap = [
  {
    time: text("现在（2026.7）", "Now — July 2026"),
    body: text("完成概念与可演示原型", "Complete the product concept and demonstrable prototype"),
  },
  {
    time: text("未来6个月", "Next 6 months"),
    body: text("上线测试网，接入真实钱包，开放早期用户试用", "Launch on testnet, integrate real wallets, and open the product to early users"),
  },
  {
    time: text("未来12个月", "Next 12 months"),
    body: text("主网上线 + 开放Agent市场 + 引入机构Solver", "Launch on mainnet, open the Agent marketplace, and introduce institutional Solvers"),
  },
  {
    time: text("未来18-24个月", "Next 18–24 months"),
    body: text("成为AI Agent交易基础设施，拓展更多金融场景", "Become AI Agent trading infrastructure and expand into additional financial use cases"),
  },
];

export const progressPoints = [
  text("产品概念与核心架构已完成", "The product concept and core architecture have been completed"),
  text("文字版介绍与可运行Web原型已完成", "The written presentation and functional web prototype have been completed"),
  text("技术方向对齐2026年最新标准（Agent身份、意图交易、跨链抽象）", "The technical direction aligns with the latest 2026 standards, including Agent identity, intent-based trading, and cross-chain abstraction"),
];

export const teamRoles = [
  text("创始人／CEO：AI + 区块链双重背景", "Founder / CEO: Background in both AI and blockchain"),
  text("技术负责人：大型交易系统与智能合约经验", "Technology Lead: Experience in large-scale trading systems and smart contracts"),
  text("AI负责人：大模型与Agent开发经验", "AI Lead: Experience in large language models and Agent development"),
  text("产品与增长：加密用户运营经验", "Product & Growth: Experience in cryptocurrency user operations"),
];

export const projections = [
  {
    timeline: text("6个月后", "After 6 months"),
    users: text("3,000-5,000", "3,000–5,000"),
    volume: text("500万-1,000万美元", "US$5–10 million"),
    revenue: text("早期手续费", "Initial transaction-fee revenue"),
    goal: text("测试网上线，验证核心体验", "Launch on testnet and validate the core experience"),
  },
  {
    timeline: text("12个月后", "After 12 months"),
    users: text("2-3万", "20,000–30,000"),
    volume: text("5,000万-1亿美元", "US$50–100 million"),
    revenue: text("开始有稳定收入", "Starts generating stable revenue"),
    goal: text("主网上线，Agent市场开放", "Launch on mainnet and open the Agent marketplace"),
  },
  {
    timeline: text("18-24个月后", "After 18–24 months"),
    users: text("8-15万", "80,000–150,000"),
    volume: text("3-8亿美元", "US$300–800 million"),
    revenue: text("可持续正向现金流", "Sustainable positive cash flow"),
    goal: text("成为AI Agent交易主要入口之一", "Become one of the primary gateways for AI Agent trading"),
  },
];

export const projectionHeaders = [
  text("时间节点", "Timeline"),
  text("活跃用户", "Active Users"),
  text("月交易量（预估）", "Estimated Monthly Trading Volume"),
  text("月收入（预估）", "Estimated Monthly Revenue"),
  text("重点目标", "Primary Objective"),
];

export const revenueMix = [
  { value: "50-60%", label: text("交易手续费", "Transaction fees"), size: 55 },
  { value: "20-25%", label: text("Agent市场抽成", "Agent marketplace commissions"), size: 23 },
  { value: "15-20%", label: text("高级订阅与数据服务", "Premium subscriptions and data services"), size: 18 },
  { value: "5-10%", label: text("其他（Solver费用等）", "Other revenue, including Solver fees"), size: 8 },
];

export const assumptions = [
  text("用户平均每月产生一定交易额", "Each user generates a certain amount of trading volume per month"),
  text("平台抽成比例保持合理", "The platform maintains a reasonable commission rate"),
  text("AI执行成功率与用户体验持续提升", "AI execution success rates and the user experience continue to improve"),
  text("安全与合规成本可控", "Security and compliance costs remain manageable"),
];

export const fundUse = [
  { value: 40, label: text("产品研发与AI优化", "Product development and AI optimization") },
  { value: 25, label: text("安全审计与合规", "Security audits and compliance") },
  { value: 20, label: text("用户增长与推广", "User growth and promotion") },
  { value: 15, label: text("团队与运营", "Team and operations") },
];

export const risks = [
  text("产品仍处早期，技术成熟度需验证", "The product remains at an early stage, and its technical maturity must still be validated"),
  text("AI判断可能出错，无法保证获利", "AI judgments may be wrong, and profitability cannot be guaranteed"),
  text("依赖外部区块链与AI基础设施稳定性", "The product depends on the stability of external blockchain and AI infrastructure"),
  text("监管对AI自主交易仍有不确定性", "Regulatory treatment of autonomous AI trading remains uncertain"),
  text("安全始终是最大挑战", "Security will always be the greatest challenge"),
];
